<template>
  <div v-if="fields && fields.length">
    <h4 class="mb-3">Campos específicos</h4>
    <div class="row">
      <component
        v-for="field in simpleFields"
        :is="resolveComponent(field)"
        :key="field.name"
        v-bind="fieldProps(field)"
        :class="columnClass"
        v-model="previewData[field.name]"
      />
      <component
        v-for="field in optionFields"
        :is="resolveComponent(field)"
        :key="field.name"
        v-bind="fieldProps(field)"
        :class="optionColumnClass"
        v-model="previewData[field.name]"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import TextInputField from '@/components/dynamicForms/typeText.vue';
import NumberInputField from '@/components/dynamicForms/typeNumber.vue';
import PasswordInputField from '@/components/dynamicForms/typePassword.vue';
import TextareaField from '@/components/dynamicForms/typeArea.vue';
import SelectField from '@/components/dynamicForms/typeSelect.vue';
import SliderField from '@/components/dynamicForms/typeSlider.vue';
import SwitchField from '@/components/dynamicForms/typeSwitch.vue';
import TimeField from '@/components/dynamicForms/typeTime.vue';
import FileUploadField from '@/components/dynamicForms/typeDoc.vue';
import ImageUploadField from '@/components/dynamicForms/typeImg.vue';
import CheckboxField from '@/components/dynamicForms/typeCheck.vue';
import RadioField from '@/components/dynamicForms/typeRadio.vue';

const props = defineProps({
  fields: {
    type: Array,
    required: true,
  },
});

const previewData = ref({});

watch(
  () => props.fields,
  (fields) => {
    const data = {};
    fields.forEach(f => {
      // Usar el tipo correcto según el tipo de campo
      if (f.as === 'input' && f.uploadType) {
        data[f.name] = [];
      } else if (f.as === 'checkbox') {
        data[f.name] = [];
      } else if (f.as === 'switch') {
        data[f.name] = false;
      } else if (f.as === 'number' || f.as === 'slider') {
        data[f.name] = 0;
      } else {
        data[f.name] = '';
      }
    });
    previewData.value = data;
  },
  { immediate: true }
);

function resolveComponent(field) {
  if (field.as === 'input') {
    if (field.uploadType === 'file') return FileUploadField;
    if (field.uploadType === 'image') return ImageUploadField;
    if (field.type === 'password') return PasswordInputField;
    if (field.type === 'textarea') return TextareaField;
    if (field.type === 'number') return NumberInputField;
    return TextInputField;
  } else if (field.as === 'number') {
    return NumberInputField;
  } else if (field.as === 'select') {
    return SelectField;
  } else if (field.as === 'slider') {
    return SliderField;
  } else if (field.as === 'switch') {
    return SwitchField;
  } else if (field.as === 'time-range') {
    return TimeField;
  } else if (field.as === 'checkbox') {
    return CheckboxField;
  } else if (field.as === 'radio') {
    return RadioField;
  }
  return TextInputField;
}

const simpleFields = computed(() =>
  props.fields.filter((f) => !['checkbox', 'radio'].includes(f.as))
);

const optionFields = computed(() =>
  props.fields.filter((f) => ['checkbox', 'radio'].includes(f.as))
);

const columnClass = computed(() => {
  return props.fields.length <= 3 ? 'col-12 text-start' : 'col-6 text-start';
});

const optionColumnClass = computed(() => {
  return optionFields.value.length <= 1 ? 'col-12 text-start' : 'col-6 text-start';
});

function fieldProps(field) {
  const baseProps = {
    id: field.name,
    name: field.name,
    label: field.label,
    required: field.required,
    validationRules: () => true, // Preview no necesita validación real
  };

  if (field.placeholder) {
    baseProps.placeholder = field.placeholder;
  }

  if (field.children && field.children.length > 0) {
    baseProps.options = field.children;
  }

  if (field.uploadType) {
    baseProps.uploadType = field.uploadType;
  }

  if (field.limit) {
    baseProps.limit = field.limit;
  }

  if (field.type) {
    baseProps.type = field.type;
  }

  if (field.fieldName) {
    baseProps.fieldName = field.fieldName;
  }

  return baseProps;
}
</script>